import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Camera, FileImage, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { run } from "./labels";

const Thumb = ({ url, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u = null;
    api.get(url, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => setSrc(""));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [url]);
  if (src === null) return <div className="h-28 w-full animate-pulse rounded bg-slate-100" />;
  if (!src) return <div className="flex h-28 w-full items-center justify-center rounded bg-slate-100 text-[11px] text-slate-500">Immagine non disponibile</div>;
  return <img src={src} alt={alt} className="h-28 w-full rounded object-cover" />;
};

const Foto = ({ f, mod, onToggle }) => (
  <label className={`block rounded-md border p-1.5 transition-opacity ${f.incluso ? "" : "opacity-45"}`} data-testid={`sal-foto-${f.allegato_id || f.id}`}>
    <Thumb url={f.url} alt={f.didascalia || f.filename} />
    <div className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-600">
      {mod && <Checkbox checked={f.incluso} onCheckedChange={(v) => onToggle(f, !!v)} data-testid={`sal-foto-check-${f.allegato_id}`} />}
      <span className="line-clamp-2">{f.didascalia || f.filename}{f.data ? ` · ${f.data.slice(0, 10)}` : ""}</span>
    </div>
  </label>
);

const Stato = ({ d }) => {
  if (d.congelato) return <span className="flex items-center gap-1 text-emerald-700" data-testid="sal-foto-stato"><Lock className="h-3 w-3" /> Fissato all'emissione{d.congelato_da ? ` da ${d.congelato_da}` : ""} · {d.n_selezionate} foto</span>;
  if (!d.disponibile) return <span className="text-slate-500" data-testid="sal-foto-stato">Nessun allegato fotografico per questo SAL.</span>;
  return <span className="text-slate-600" data-testid="sal-foto-stato">{d.n_selezionate} di {d.n_foto} foto selezionate · l'elenco viene fissato all'emissione</span>;
};

export const SalFoto = ({ base, nomeFile }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`${base}/foto`).then((r) => setD(r.data)).catch(() => setD(false)); }, [base]);
  const toggle = async (f, incluso) => {
    const r = await run(() => api.put(`${base}/foto/${f.allegato_id}`, { incluso }));
    if (r) setD(r.data);
  };
  const pdf = async () => {
    const r = await run(() => api.get(`${base}/foto.pdf`, { responseType: "blob" }));
    if (!r) return;
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = `${nomeFile}.pdf`; a.click();
    URL.revokeObjectURL(u);
  };
  if (d === false) return null;
  return (
    <section className="space-y-3 rounded-md border p-3" data-testid="sal-foto">
      <div className="flex flex-wrap items-center gap-2">
        <Camera className="h-4 w-4 text-sky-700" />
        <h3 className="text-sm font-semibold">Allegato fotografico</h3>
        {d && <div className="text-xs"><Stato d={d} /></div>}
        <Button size="sm" variant="outline" className="ml-auto" onClick={pdf} disabled={!d} data-testid="sal-foto-pdf"><FileImage className="mr-1 h-3.5 w-3.5" /> PDF fotografico</Button>
      </div>
      {d && d.disponibile && d.voci.filter((v) => v.foto.length).length === 0 && <p className="text-xs text-slate-500" data-testid="sal-foto-vuoto">Le voci di questo SAL non hanno fotografie.</p>}
      {d && d.voci.filter((v) => v.foto.length).map((v) => (
        <div key={v.riga_computo_id} className="space-y-1.5" data-testid={`sal-foto-voce-${v.riga_computo_id}`}>
          <p className="text-xs font-medium text-slate-700">N. {v.num_ord || "—"} · {v.tariffa} — <span className="font-normal">{(v.designazione || "").slice(0, 140)}</span></p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {v.foto.map((f) => <Foto key={f.id || f.allegato_id} f={f} mod={d.modificabile} onToggle={toggle} />)}
          </div>
        </div>
      ))}
    </section>
  );
};
